import React, { useEffect, useMemo, useState } from "react";
import { ShieldAlert, Search, Download, RefreshCw, Copy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SectionHeader } from "@/components/SectionHeader";

// Client-side filter/paginate over the latest N=500 rows returned by
// `GET /api/audit-logs`. No new backend endpoint is added. Super Admin
// only; Standard Admin does NOT see this page.

const PAGE_SIZE = 25;
const FETCH_LIMIT = 500;

const NotAuthorized = () => (
  <div className="max-w-2xl mx-auto p-8" data-testid="audit-log-not-authorized">
    <div className="border border-critical-red rounded-sm bg-white p-6">
      <div className="flex items-center gap-2 text-critical-red font-semibold text-[14px] mb-2">
        <ShieldAlert size={18} /> Not authorized
      </div>
      <p className="text-[13px] text-[color:var(--fg-secondary)]">
        The Audit Log is restricted to Super Admin accounts.
      </p>
    </div>
  </div>
);

const fmtLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const summarize = (row) => {
  const a = row.action || row.event_type || "";
  const t = row.entity_type ? ` · ${row.entity_type}` : "";
  const eid = row.entity_id ? ` · ${String(row.entity_id).slice(0, 8)}` : "";
  return `${a}${t}${eid}`;
};

const toCsv = (rows) => {
  const header = [
    "timestamp", "actor_email", "actor_role",
    "action", "entity_type", "entity_id",
    "before", "after",
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      esc(r.ts),
      esc(r.actor_email),
      esc(r.actor_role),
      esc(r.action || r.event_type),
      esc(r.entity_type),
      esc(r.entity_id),
      esc(r.before),
      esc(r.after),
    ].join(","));
  }
  return lines.join("\n");
};

export default function AuditLogPage() {
  const { isSuperAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  // filters
  const [actorFilter, setActorFilter] = useState("");
  const [entityType, setEntityType] = useState("");
  const [eventType, setEventType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/audit-logs", { params: { limit: FETCH_LIMIT } });
      const data = Array.isArray(r.data) ? r.data : [];
      setRows(data);
      setLoadedAt(new Date());
    } catch (ex) {
      setErr({
        status: ex?.response?.status,
        detail: ex?.response?.data?.detail || ex.message,
      });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const entityTypeOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.entity_type) s.add(r.entity_type);
    return Array.from(s).sort();
  }, [rows]);

  const eventTypeOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      const a = r.action || r.event_type;
      if (a) s.add(a);
    }
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const actorQ = actorFilter.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom).getTime() : null;
    const to = dateTo ? new Date(dateTo).getTime() + 86_400_000 - 1 : null;
    return rows.filter((r) => {
      if (entityType && r.entity_type !== entityType) return false;
      const act = r.action || r.event_type || "";
      if (eventType && act !== eventType) return false;
      if (actorQ) {
        const hay = `${r.actor_email || ""} ${r.actor_id || ""}`.toLowerCase();
        if (!hay.includes(actorQ)) return false;
      }
      if (from || to) {
        const t = r.ts ? new Date(r.ts).getTime() : null;
        if (t == null || Number.isNaN(t)) return false;
        if (from && t < from) return false;
        if (to && t > to) return false;
      }
      return true;
    });
  }, [rows, actorFilter, entityType, eventType, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [actorFilter, entityType, eventType, dateFrom, dateTo]);

  const exportCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `audit-log-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      /* noop */
    }
  };

  const toggleExpand = (rowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  if (!isSuperAdmin) return <NotAuthorized />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="audit-log-page">
      {/* RC4 · SectionHeader adoption landmark. */}
      <SectionHeader testid="page-section-landmark" className="sr-only">Audit Log</SectionHeader>
      <div className="mb-4">
        <h1
          className="text-[22px] font-semibold"
          style={{ fontFamily: "Montserrat, Inter, sans-serif" }}
        >
          Audit Log
        </h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--fg-secondary)" }}>
          Super Admin only. Read-only view of platform activity. Loaded the most
          recent {FETCH_LIMIT} events; filter and paginate client-side.
        </p>
        <p
          className="text-[11.5px] mt-1 uppercase tracking-widest"
          style={{ color: "var(--fg-muted)" }}
          data-testid="audit-log-readonly-notice"
        >
          READ-ONLY — cannot be edited or deleted.
        </p>
      </div>

      {/* Filters */}
      <div
        className="border rounded-sm bg-white mb-4"
        style={{ borderColor: "var(--border-default)" }}
        data-testid="audit-log-filters"
      >
        <div className="p-4 grid md:grid-cols-6 gap-3 text-[12.5px]">
          <label className="md:col-span-2 flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
              Actor (email or id)
            </span>
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--fg-muted)]"
              />
              <input
                data-testid="audit-log-filter-actor"
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                placeholder="admin@allaccess.test"
                className="w-full h-9 border rounded-sm pl-7 pr-2"
                style={{ borderColor: "var(--border-default)" }}
              />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
              Entity type
            </span>
            <select
              data-testid="audit-log-filter-entity-type"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="h-9 border rounded-sm px-2"
              style={{ borderColor: "var(--border-default)" }}
            >
              <option value="">All</option>
              {entityTypeOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
              Event type
            </span>
            <select
              data-testid="audit-log-filter-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="h-9 border rounded-sm px-2"
              style={{ borderColor: "var(--border-default)" }}
            >
              <option value="">All</option>
              {eventTypeOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
              From
            </span>
            <input
              type="date"
              data-testid="audit-log-filter-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 border rounded-sm px-2"
              style={{ borderColor: "var(--border-default)" }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
              To
            </span>
            <input
              type="date"
              data-testid="audit-log-filter-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 border rounded-sm px-2"
              style={{ borderColor: "var(--border-default)" }}
            />
          </label>
        </div>
        <div
          className="px-4 py-2 border-t flex items-center gap-2 flex-wrap text-[12.5px]"
          style={{
            borderColor: "var(--border-subtle)",
            background: "var(--bg-surface)",
          }}
        >
          <button
            type="button"
            data-testid="audit-log-refresh"
            onClick={load}
            disabled={busy}
            className="h-8 px-3 rounded-sm border inline-flex items-center gap-1 disabled:opacity-60"
            style={{ borderColor: "var(--border-default)" }}
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            Refresh
          </button>
          <button
            type="button"
            data-testid="audit-log-export-csv"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="h-8 px-3 rounded-sm text-white inline-flex items-center gap-1 disabled:opacity-40"
            style={{ background: "var(--brand-blue, #1B4F8A)" }}
          >
            <Download size={12} />
            Export CSV
          </button>
          <div className="ml-auto text-[color:var(--fg-muted)]" data-testid="audit-log-status">
            {loadedAt && (
              <>
                Loaded {rows.length} rows at {fmtLocal(loadedAt.toISOString())} ·
                {" "}
                Showing {filtered.length} after filters
              </>
            )}
          </div>
        </div>
      </div>

      {err && (
        <div
          className="mb-3 text-[12.5px] text-critical-red"
          data-testid="audit-log-error"
        >
          Load failed{err.status ? ` (HTTP ${err.status})` : ""}:{" "}
          {typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail)}
        </div>
      )}

      {/* Table */}
      <div
        className="border rounded-sm bg-white overflow-hidden"
        style={{ borderColor: "var(--border-default)" }}
      >
        <table className="w-full text-[12.5px]" data-testid="audit-log-table">
          <thead style={{ background: "var(--bg-surface)" }}>
            <tr>
              <th className="px-3 py-2 text-left w-[170px]">Timestamp</th>
              <th className="px-3 py-2 text-left">Actor</th>
              <th className="px-3 py-2 text-left">Event</th>
              <th className="px-3 py-2 text-left">Entity</th>
              <th className="px-3 py-2 text-left w-[180px]">Entity ID</th>
              <th className="px-3 py-2 text-left">Summary</th>
              <th className="px-3 py-2 text-right w-[60px]">Details</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-[color:var(--fg-muted)]"
                  data-testid="audit-log-empty"
                >
                  {busy ? "Loading…" : "No matching audit events."}
                </td>
              </tr>
            )}
            {pageRows.map((r, i) => {
              const key = `${r.id || r.ts || i}-${i}`;
              const isOpen = expanded.has(key);
              const evt = r.action || r.event_type || "";
              return (
                <React.Fragment key={key}>
                  <tr
                    className="border-t"
                    style={{ borderColor: "var(--border-subtle)" }}
                    data-testid={`audit-log-row-${i}`}
                  >
                    <td className="px-3 py-1.5 font-mono text-[11.5px]">
                      {fmtLocal(r.ts)}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="truncate max-w-[200px]">
                        {r.actor_email || (
                          <span className="text-[color:var(--fg-muted)]">system</span>
                        )}
                      </div>
                      {r.actor_role && (
                        <div
                          className="text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          {r.actor_role}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span
                        className="font-mono text-[11.5px] px-1.5 py-0.5 rounded-sm"
                        style={{
                          background: "var(--bg-surface)",
                          border: "1px solid var(--border-subtle)",
                        }}
                      >
                        {evt}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">{r.entity_type || ""}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">
                      {r.entity_id ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 hover:underline"
                          onClick={() => copyId(r.entity_id)}
                          title="Click to copy"
                          data-testid={`audit-log-copy-entity-${i}`}
                        >
                          {String(r.entity_id).slice(0, 12)}
                          <Copy size={10} />
                        </button>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="truncate max-w-[300px]">{summarize(r)}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {(r.before || r.after) && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(key)}
                          data-testid={`audit-log-expand-${i}`}
                          className="text-[11px] px-2 h-6 rounded-sm border"
                          style={{ borderColor: "var(--border-default)" }}
                        >
                          {isOpen ? "Hide" : "View"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr
                      style={{ background: "var(--bg-surface)" }}
                      data-testid={`audit-log-details-${i}`}
                    >
                      <td colSpan={7} className="px-3 py-2">
                        <div className="grid md:grid-cols-2 gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-[color:var(--fg-muted)] mb-1">
                              Before
                            </div>
                            <pre
                              className="text-[11px] bg-white p-2 rounded-sm border overflow-x-auto max-h-64"
                              style={{ borderColor: "var(--border-subtle)" }}
                            >
{JSON.stringify(r.before ?? null, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-[color:var(--fg-muted)] mb-1">
                              After
                            </div>
                            <pre
                              className="text-[11px] bg-white p-2 rounded-sm border overflow-x-auto max-h-64"
                              style={{ borderColor: "var(--border-subtle)" }}
                            >
{JSON.stringify(r.after ?? null, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div
            className="px-3 py-2 border-t flex items-center gap-2 text-[12px]"
            style={{
              borderColor: "var(--border-subtle)",
              background: "var(--bg-surface)",
            }}
          >
            <button
              type="button"
              data-testid="audit-log-page-prev"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageSafe <= 1}
              className="h-7 px-2 rounded-sm border disabled:opacity-40"
              style={{ borderColor: "var(--border-default)" }}
            >
              Prev
            </button>
            <span data-testid="audit-log-page-indicator">
              Page {pageSafe} of {totalPages}
            </span>
            <button
              type="button"
              data-testid="audit-log-page-next"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageSafe >= totalPages}
              className="h-7 px-2 rounded-sm border disabled:opacity-40"
              style={{ borderColor: "var(--border-default)" }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
